import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import {
  Trophy,
  User,
  ChartBar,
  Target,
  TrendUp,
  Medal
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedStructure, setSelectedStructure] = useState('');
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API_URL}/api/branches`).then(res => setBranches(res.data)).catch(() => {});
    }
    axios.get(`${API_URL}/api/data-structures`).then(res => setStructures(res.data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedBranch, selectedStructure]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append('branch_id', selectedBranch);
      if (selectedStructure) params.append('structure_id', selectedStructure);
      const { data: res } = await axios.get(`${API_URL}/api/analytics/leaderboard?${params}`);
      setData(res);
    } catch (e) {
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#1E3A2B', '#D95D39', '#457B9D', '#F4A261', '#2A9D8F', '#E76F51', '#264653', '#E9C46A'];

  const getRankBadge = (index) => {
    if (index === 0) return <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center"><Medal size={16} weight="fill" className="text-white" /></div>;
    if (index === 1) return <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center"><Medal size={16} weight="fill" className="text-white" /></div>;
    if (index === 2) return <div className="w-7 h-7 rounded-full bg-amber-700 flex items-center justify-center"><Medal size={16} weight="fill" className="text-white" /></div>;
    return <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground">{index + 1}</div>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton"></div>
        <div className="h-64 skeleton rounded-md"></div>
        <div className="h-96 skeleton rounded-md"></div>
      </div>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const chartData = leaderboard.filter(u => u.assigned > 0).slice(0, 10);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy size={28} weight="duotone" className="text-amber-500" />
            Leaderboard
          </h1>
          <p className="text-muted-foreground">Team performance metrics and rankings</p>
        </div>
        {user?.role === 'super_admin' && (
          <div className="w-56">
            <Label className="text-xs text-muted-foreground mb-1 block">Branch</Label>
            <Select value={selectedBranch || "all"} onValueChange={(v) => setSelectedBranch(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="leaderboard-branch-filter">
                <SelectValue placeholder="All branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-56">
          <Label className="text-xs text-muted-foreground mb-1 block">Schema</Label>
          <Select value={selectedStructure || "all"} onValueChange={(v) => setSelectedStructure(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="leaderboard-schema-filter">
              <SelectValue placeholder="All schemas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Schemas</SelectItem>
              {structures.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Records</p>
            <p className="text-2xl font-bold" data-testid="lb-total-records">{data?.total_records || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Unassigned</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="lb-unassigned">{data?.unassigned || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Team Members</p>
            <p className="text-2xl font-bold">{leaderboard.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Avg Conversion</p>
            <p className="text-2xl font-bold text-primary">
              {leaderboard.length > 0 ? (leaderboard.reduce((s, u) => s + u.conversion_rate, 0) / leaderboard.length).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card data-testid="leaderboard-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ChartBar size={20} /> Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  tickFormatter={(v) => v.split(' ')[0]}
                />
                <YAxis tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="assigned" name="Assigned" fill="#1E3A2B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="closed" name="Closed" fill="#2A9D8F" radius={[4, 4, 0, 0]} />
                <Bar dataKey="interactions" name="Interactions" fill="#D95D39" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy size={20} /> Rankings
          </CardTitle>
          <CardDescription>Sorted by assigned records</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Rank</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                {user?.role === 'super_admin' && !selectedBranch && <TableHead>Branch</TableHead>}
                <TableHead className="text-center">Assigned</TableHead>
                <TableHead className="text-center">Closed</TableHead>
                <TableHead className="text-center">Open</TableHead>
                <TableHead className="text-center">Interactions</TableHead>
                <TableHead className="text-center">Conversion</TableHead>
                <TableHead className="text-center">Volume %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaderboard.length > 0 ? leaderboard.map((entry, index) => (
                <TableRow key={entry.user_id} data-testid={`lb-row-${entry.user_id}`}>
                  <TableCell>{getRankBadge(index)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{entry.name}</p>
                      <p className="text-xs text-muted-foreground">{entry.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">
                      {entry.role.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  {user?.role === 'super_admin' && !selectedBranch && (
                    <TableCell className="text-sm">{entry.branch_name}</TableCell>
                  )}
                  <TableCell className="text-center font-semibold">{entry.assigned}</TableCell>
                  <TableCell className="text-center">
                    <span className="text-green-600 font-medium">{entry.closed}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={entry.open > 0 ? 'text-amber-600' : 'text-muted-foreground'}>{entry.open}</span>
                  </TableCell>
                  <TableCell className="text-center">{entry.interactions}</TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={entry.conversion_rate >= 50 ? 'default' : 'outline'}
                      className={entry.conversion_rate >= 50 ? 'bg-green-600' : ''}
                    >
                      {entry.conversion_rate}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${Math.min(entry.volume_pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{entry.volume_pct}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    <User size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No team members found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardPage;
